import { Badge, type BadgeVariant } from "@fox1ck-org/ui";
import { LanderType, RiskStatus } from "@gen/tracker/v1/lander_pb";

import { useT, type TKey } from "@/lib/i18n";

/**
 * What a page is and how risky it is to show — the two words every lander
 * screen carries (boards LanderList, LanderTiles, LanderCard). The page types
 * are the landing studio's, in the order a person reads them.
 */
export const LANDER_TYPES: readonly LanderType[] = [
  LanderType.LANDING,
  LanderType.PRELANDING,
  LanderType.VSL,
  LanderType.WHITE_PAGE,
  LanderType.THANK_YOU,
];

/** The URL word of a type (`?type=vsl`) and back. */
export const TYPE_SLUG: Record<LanderType, string> = {
  [LanderType.UNSPECIFIED]: "",
  [LanderType.LANDING]: "landing",
  [LanderType.PRELANDING]: "prelanding",
  [LanderType.VSL]: "vsl",
  [LanderType.WHITE_PAGE]: "white",
  [LanderType.THANK_YOU]: "thanks",
};

export function typeFromSlug(slug: string): LanderType | undefined {
  return LANDER_TYPES.find((t) => TYPE_SLUG[t] === slug);
}

const TYPE_LABEL: Record<LanderType, TKey> = {
  [LanderType.UNSPECIFIED]: "landers.type.landing",
  [LanderType.LANDING]: "landers.type.landing",
  [LanderType.PRELANDING]: "landers.type.prelanding",
  [LanderType.VSL]: "landers.type.vsl",
  [LanderType.WHITE_PAGE]: "landers.type.white",
  [LanderType.THANK_YOU]: "landers.type.thanks",
};

/** The short word a segmented control has room for («Белая», not «Белая страница»). */
const TYPE_SHORT: Record<LanderType, TKey> = {
  ...TYPE_LABEL,
  [LanderType.WHITE_PAGE]: "landers.type.whiteShort",
};

const TYPE_VARIANT: Record<LanderType, BadgeVariant> = {
  [LanderType.UNSPECIFIED]: "primary",
  [LanderType.LANDING]: "primary",
  [LanderType.PRELANDING]: "primary",
  [LanderType.VSL]: "primary",
  [LanderType.WHITE_PAGE]: "info",
  [LanderType.THANK_YOU]: "neutral",
};

export const typeLabelKey = (t: LanderType): TKey => TYPE_LABEL[t] ?? TYPE_LABEL[LanderType.LANDING];
export const typeShortKey = (t: LanderType): TKey => TYPE_SHORT[t] ?? TYPE_SHORT[LanderType.LANDING];

export function LanderTypeBadge({ type, isDefault, size = "sm" }: { type: LanderType; isDefault?: boolean; size?: "sm" | "md" }) {
  const t = useT();
  return (
    <Badge variant={TYPE_VARIANT[type] ?? "primary"} size={size} className="whitespace-nowrap">
      {t(typeLabelKey(type))}
      {type === LanderType.THANK_YOU && isDefault ? ` · ${t("landers.type.default")}` : ""}
    </Badge>
  );
}

export const RISKS: readonly RiskStatus[] = [RiskStatus.SAFE, RiskStatus.WARN, RiskStatus.DANGER];

export const RISK_SLUG: Record<RiskStatus, string> = {
  [RiskStatus.UNSPECIFIED]: "",
  [RiskStatus.SAFE]: "safe",
  [RiskStatus.WARN]: "warn",
  [RiskStatus.DANGER]: "danger",
};

export function riskFromSlug(slug: string): RiskStatus | undefined {
  return RISKS.find((r) => RISK_SLUG[r] === slug);
}

const RISK_LABEL: Record<RiskStatus, TKey> = {
  [RiskStatus.UNSPECIFIED]: "landers.risk.safe",
  [RiskStatus.SAFE]: "landers.risk.safe",
  [RiskStatus.WARN]: "landers.risk.warn",
  [RiskStatus.DANGER]: "landers.risk.danger",
};

const RISK_VARIANT: Record<RiskStatus, BadgeVariant> = {
  [RiskStatus.UNSPECIFIED]: "ok",
  [RiskStatus.SAFE]: "ok",
  [RiskStatus.WARN]: "warn",
  [RiskStatus.DANGER]: "danger",
};

export const riskLabelKey = (r: RiskStatus): TKey => RISK_LABEL[r] ?? RISK_LABEL[RiskStatus.SAFE];

/** «● Безопасно» / «● Проверить» / «● Опасно». */
export function RiskBadge({ risk, size = "sm" }: { risk: RiskStatus; size?: "sm" | "md" }) {
  const t = useT();
  return (
    <Badge variant={RISK_VARIANT[risk] ?? "ok"} size={size} dot className="whitespace-nowrap">
      {t(riskLabelKey(risk))}
    </Badge>
  );
}

/** A country or language code as the boards draw it: a small mono chip. */
export function CodeChip({ code }: { code: string }) {
  return (
    <span className="inline-flex h-5 items-center rounded-sm border border-border bg-surface-2 px-1.5 font-mono text-xs uppercase text-muted-foreground">
      {code}
    </span>
  );
}
